import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SystemSettingsGetSchema} from '../../../../api/DBSchema/SystemSettings';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import Sepperator from '../../../../components/Simple/Sepperator';
import SMCButton from '../../../../components/Simple/SMCButton';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import RowLayout from '../../../../components/Layouts/RowLayout';
import Api from '../../../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../../../utils/errorUtils';
import SystemLicenseEditorModal from '../../../PopupPages/SystemLicenseEditorModal';
import {useBoolean} from '../../../../utils/hooks';
import {replaceItemInIndex} from '../../../../utils/utils';
import SMCLink from '../../../../components/Simple/SMCLink';

const pageSchema = SystemSettingsGetSchema.pages.licenseManagement;
const pageId = pageSchema.pageId;

// TODO: This page must be tested and refined on production
function SystemSettingsLicenseManagement({
    systemSettingsObject
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const pageData = systemSettingsObject[pageId];

    const numManagedGWs   = pageData[pageSchema.numManagedGWs];
    const leftNumLicenses = pageData[pageSchema.leftNumLicenses];

    const [licensesList, setLicensesList] = useState(null);
    const [isFetchingLicensesList, setIsFetchingLicensesList] = useState(false);
    const [isApplyingLicensesList, setIsApplyingLicensesList] = useState(false);
    const [hasApplyError, setHasApplyError] = useState(false);
    
    const licensesListSchema = pageSchema.licensesListSchema;

    const [isLicenseEditorModalOpen, openLicenseEditorModal, closeLicenseEditorModal] = useBoolean(false);
    const [editObject, setEditObject] = useState(null);
    const [editObjectIndex, setEditObjectIndex] = useState(null);

    const CountCell = useCallback(({object, index}) => {
        return (
            <SMCLink
                inline
                onClick={() => {
                    setEditObject(object);
                    setEditObjectIndex(index);
                    openLicenseEditorModal();
                }}
            >
                {index+1}
            </SMCLink>
        )
    }, [openLicenseEditorModal]);

    const SkuCell = useCallback(({value})  => {
        return <span>{ Array.isArray(value) ? value.join(', ') : value }</span>;
    }, []);

    const licenseEditorOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectIndex(null);
        closeLicenseEditorModal();
    }, [closeLicenseEditorModal]);

    const licenseEditorNewCallback = useCallback((newLicense) => {
        setLicensesList(list => [...list, newLicense]);
        closeLicenseEditorModal();
    }, [closeLicenseEditorModal]);

    const licenseEditorEditCallback = useCallback((newLicense) => {
        setLicensesList(list => replaceItemInIndex(list, newLicense, editObjectIndex))
        closeLicenseEditorModal();
    }, [editObjectIndex, closeLicenseEditorModal]);

    const getLicenseList = useCallback(() => {
        setIsFetchingLicensesList(true);
        Api.getLicenseManagementList()
        .then(resp => {
            const respData = resp.data.data;
            const _licensesList = respData.licensesList;
            setLicensesList(_licensesList);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        })
        .finally(() => {
            setIsFetchingLicensesList(false);
        })
    }, [getErrorMessage]);

    const applyLicenseList = useCallback(() => {
        if (licensesList && licensesList.length) {
            setIsApplyingLicensesList(true);
            Api.setLicenseManagementList(licensesList)
            // .then(resp => {})
            .catch(() => {
                setHasApplyError(true);
            })
            .finally(() => {
                setIsApplyingLicensesList(false);
            });
        }
    }, [licensesList]);

    return (
        <EditorLayout labelWidth={400}>
            <b>
                <SMCTextInput
                    readOnly
                    value={numManagedGWs}
                    label={t('SMP.SETTINGS.LICENSE_MANAGEMENT.LINE1')+':'}
                />
                <SMCTextInput
                    readOnly
                    value={leftNumLicenses}
                    label={t('SMP.SETTINGS.LICENSE_MANAGEMENT.LINE2')+':'}
                />
            </b>
            <Sepperator />
            <RowLayout spacing="4px">
                <SMCButton disabled={isFetchingLicensesList} ignoreDisabledContext onClick={getLicenseList}>{t('SMP.SETTINGS.LICENSE_MANAGEMENT.VIEW_LICENSES')}</SMCButton>
                <SMCButton disabled={!licensesList || isApplyingLicensesList} onClick={applyLicenseList}>{t('SMP.SETTINGS.LICENSE_MANAGEMENT.APPLY_LICENSES')}</SMCButton>
            </RowLayout>
            <SMCLabel bold>
                {
                    hasApplyError ? 'Error adding SMP license: See errors under \'Logs > System Logs\'' : <>&nbsp;</>
                }
            </SMCLabel>
            {
                licensesList && (
                    <>
                    <SMCButton variant="ghost" icon="new" onClick={openLicenseEditorModal}>{t('SMP.SETTINGS.LICENSE_MANAGEMENT.ADD_LICENSE')}</SMCButton>
                    <SMCTable
                        columns={[
                            { id: 'count',            header: '#',                                                  width: 32,                                                align: 'center', Cell: CountCell  },
                            { id: 'empty',            header: '',                                                   width: 32                                                                                   },
                            { id: 'ip',               header: t('SMP.SETTINGS.LICENSE_MANAGEMENT.IP_ADDRESS'),      width: 110, accessor: licensesListSchema.ip                                                 },
                            { id: 'expiration',       header: t('SMP.SETTINGS.LICENSE_MANAGEMENT.EXPIRATION_DATE'), width: 80,  accessor: licensesListSchema.expirationDate                                     },
                            { id: 'signature',        header: t('SMP.SETTINGS.LICENSE_MANAGEMENT.SIGNATURE'),       width: 300, accessor: licensesListSchema.signature                                          },
                            { id: 'numberOfGateways', header: t('SMP.SETTINGS.LICENSE_MANAGEMENT.NUM_OF_GW'),       width: 85,  accessor: licensesListSchema.numberOfGateways                                   },
                            { id: 'sku',              header: t('SMP.SETTINGS.LICENSE_MANAGEMENT.SKU'),             width: 105, accessor: licensesListSchema.featureList,                         Cell: SkuCell }
                        ]}
                        content={licensesList}
                        hasCheckboxColumn={false}
                        height={200}
                    />    
                    </>
                )
            }
            <div style={{ height: 20 }} />
            <SMCLabel><b>{t('SMP.SETTINGS.LICENSE_MANAGEMENT.NOTE')}</b> {t('SMP.SETTINGS.LICENSE_MANAGEMENT.LINE3')}</SMCLabel>
            <SMCLabel>{t('SMP.SETTINGS.LICENSE_MANAGEMENT.LINE4')}</SMCLabel>
            <SystemLicenseEditorModal
                isOpen={isLicenseEditorModalOpen}
                onRequestClose={licenseEditorOnCancel}
                onSuccess={editObject ? licenseEditorEditCallback : licenseEditorNewCallback}
                defaultObject={editObject}
            />
        </EditorLayout>
    )
}

export default SystemSettingsLicenseManagement;